import React, { useEffect } from 'react';
import { 
  Container, 
  Typography, 
  Box, 
  Breadcrumbs, 
  Link, 
  Paper, 
  Card,
  CardContent,
  Chip,
  Avatar,
  Grid
} from '@mui/material';
import { 
  NavigateNext, 
  Gavel, 
  AccountBalance, 
  Shield, 
  Verified,
  Business,
  ContactMail,
  Warning,
  Assignment,
  Security,
  Policy
} from '@mui/icons-material';
import { useThemeMode } from '../contexts/ThemeContext';

function TermsOfService() {
  const { isLight } = useThemeMode();
  
  // Scroll to top when component mounts
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, []);
  
  return (
    <Box 
      sx={{ 
        pt: { xs: 10, md: 12 }, 
        pb: 8,
        background: isLight 
          ? 'linear-gradient(135deg, #f8fafc 0%, #ffffff 50%, #f1f5f9 100%)'
          : 'linear-gradient(135deg, #020617 0%, #0f172a 30%, #1e293b 60%, #334155 70%, #0f172a 90%, #020617 100%)',
        minHeight: '100vh'
      }}
    >
      <Container maxWidth="lg">
        {/* Breadcrumb Navigation */}
        <Card
          elevation={0}
          sx={{
            mb: 4,
            background: isLight
              ? 'rgba(255, 255, 255, 0.9)'
              : 'rgba(15, 23, 42, 0.8)',
            backdropFilter: 'blur(20px)',
            border: isLight
              ? '1px solid rgba(99, 102, 241, 0.1)'
              : '1px solid rgba(16, 185, 129, 0.2)',
            borderRadius: 3,
            p: 2
          }}
        >
          <Breadcrumbs 
            separator={<NavigateNext fontSize="small" sx={{ color: isLight ? '#6366f1' : '#10b981' }} />} 
            aria-label="breadcrumb"
          >
            <Link 
              color="inherit" 
              href="/#home"
              sx={{ 
                color: isLight ? '#64748b' : '#94a3b8',
                textDecoration: 'none',
                '&:hover': { color: isLight ? '#6366f1' : '#10b981' }
              }}
            >
              Home
            </Link>
            <Typography sx={{ color: isLight ? '#6366f1' : '#10b981', fontWeight: 600 }}>
              Terms of Service
            </Typography>
          </Breadcrumbs>
        </Card>
        
        {/* Header Section */}
        <Paper
          elevation={0}
          sx={{
            mb: 6,
            p: { xs: 4, md: 6 },
            background: isLight
              ? 'linear-gradient(135deg, #1e3a5f 0%, #4f46e5 50%, #6366f1 100%)'
              : 'linear-gradient(135deg, #10b981 0%, #059669 50%, #047857 100%)',
            color: 'white',
            borderRadius: 4,
            position: 'relative',
            overflow: 'hidden',
            '&::before': {
              content: '""',
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              background: `
                radial-gradient(circle at 30% 50%, rgba(255, 255, 255, 0.1) 0%, transparent 50%),
                radial-gradient(circle at 70% 20%, rgba(255, 255, 255, 0.05) 0%, transparent 50%)
              `,
              zIndex: 1,
            },
          }}
        >
          <Box sx={{ position: 'relative', zIndex: 2 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
              <Avatar
                sx={{
                  width: 64,
                  height: 64,
                  background: 'rgba(255, 255, 255, 0.2)',
                  mr: 3,
                  backdropFilter: 'blur(10px)'
                }}
              >
                <Gavel sx={{ color: 'white', fontSize: '2rem' }} />
              </Avatar>
              <Box>
                <Typography variant="overline" sx={{ opacity: 0.9, letterSpacing: 2 }}>
                  ALPHANETIX LEGAL
                </Typography>
                <Typography 
                  variant="h2" 
                  component="h1" 
                  sx={{ 
                    fontWeight: 800,
                    fontSize: { xs: '2rem', md: '3rem' },
                    lineHeight: 1.2,
                    mb: 1
                  }}
                >
                  Terms of Service
                </Typography>
              </Box>
            </Box>
            
            <Typography variant="h6" sx={{ opacity: 0.95, mb: 2, maxWidth: '800px' }}>
              These terms govern your use of our enterprise integration platform and services. 
              Please read carefully as they contain important legal rights and responsibilities.
            </Typography>
            
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}>
              <Chip
                icon={<AccountBalance sx={{ color: 'white !important' }} />}
                label="Legally Binding"
                sx={{
                  backgroundColor: 'rgba(255, 255, 255, 0.2)',
                  color: 'white',
                  fontWeight: 600,
                  '& .MuiChip-icon': { color: 'white' }
                }}
              />
              <Chip
                icon={<Shield sx={{ color: 'white !important' }} />}
                label="User Protection"
                sx={{
                  backgroundColor: 'rgba(255, 255, 255, 0.2)',
                  color: 'white',
                  fontWeight: 600
                }}
              />
              <Chip
                icon={<Business sx={{ color: 'white !important' }} />}
                label="Enterprise Grade"
                sx={{
                  backgroundColor: 'rgba(255, 255, 255, 0.2)',
                  color: 'white',
                  fontWeight: 600
                }}
              />
            </Box>
          </Box>
        </Paper>

        {/* Last Updated */}
        <Card
          elevation={0}
          sx={{
            mb: 6,
            background: isLight
              ? 'rgba(99, 102, 241, 0.05)'
              : 'rgba(16, 185, 129, 0.1)',
            border: `1px solid ${isLight ? 'rgba(99, 102, 241, 0.2)' : 'rgba(16, 185, 129, 0.3)'}`,
            borderRadius: 3,
            p: 3
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <Warning sx={{ color: isLight ? '#6366f1' : '#10b981', mr: 2, fontSize: '1.5rem' }} />
            <Box>
              <Typography variant="subtitle1" sx={{ fontWeight: 700, color: isLight ? '#1e293b' : '#ffffff', mb: 0.5 }}>
                Last Updated: August 10, 2025
              </Typography>
              <Typography variant="body2" sx={{ color: isLight ? '#64748b' : '#94a3b8' }}>
                These terms are effective immediately. By using our services, you agree to be bound by these terms.
              </Typography>
            </Box>
          </Box>
        </Card>
        
        {/* Introduction */}
        <Card
          elevation={0}
          sx={{
            mb: 6,
            background: isLight
              ? 'rgba(255, 255, 255, 0.9)'
              : 'rgba(15, 23, 42, 0.8)',
            backdropFilter: 'blur(20px)',
            border: isLight
              ? '1px solid rgba(99, 102, 241, 0.1)'
              : '1px solid rgba(16, 185, 129, 0.2)',
            borderRadius: 3,
            p: 4
          }}
        >
          <Typography variant="h6" sx={{ color: isLight ? '#1e293b' : '#ffffff', mb: 2, fontWeight: 700 }}>
            Welcome to Alphanetix
          </Typography>
          <Typography paragraph sx={{ color: isLight ? '#64748b' : '#94a3b8', fontSize: '1.1rem', lineHeight: 1.7 }}>
            These Terms of Service ("Terms") constitute a legally binding agreement between you and Alphanetix Inc. ("Company," "we," "our," or "us") 
            regarding your access to and use of our enterprise integration platform, website, and related services ("Services"). 
            By accessing or using our Services, you acknowledge that you have read, understood, and agree to be bound by these Terms.
          </Typography>
        </Card>

        {/* Main Content Sections */}
        <Grid container spacing={4}>
          {/* Section 1: Agreement & Acceptance */}
          <Grid item xs={12}>
            <Card
              elevation={0}
              sx={{
                background: isLight
                  ? 'rgba(255, 255, 255, 0.9)'
                  : 'rgba(15, 23, 42, 0.8)',
                backdropFilter: 'blur(20px)',
                border: isLight
                  ? '1px solid rgba(99, 102, 241, 0.1)'
                  : '1px solid rgba(16, 185, 129, 0.2)',
                borderRadius: 3,
                p: 4
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                <Avatar
                  sx={{
                    width: 48,
                    height: 48,
                    background: isLight ? 'linear-gradient(135deg, #6366f1 0%, #1e3a5f 100%)' : 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
                    mr: 2
                  }}
                >
                  <Assignment sx={{ color: 'white' }} />
                </Avatar>
                <Typography variant="h5" component="h2" sx={{ fontWeight: 700, color: isLight ? '#1e293b' : '#ffffff' }}>
                  1. Agreement to Terms
                </Typography>
              </Box>
              
              <Typography paragraph sx={{ color: isLight ? '#64748b' : '#94a3b8', mb: 3 }}>
                By accessing, browsing, or using our Services in any manner, you confirm that:
              </Typography>
              
              <Box component="ul" sx={{ pl: 4, mb: 3, '& li': { mb: 2 } }}>
                <Box component="li">
                  <Typography sx={{ color: isLight ? '#64748b' : '#94a3b8' }}>
                    You have the legal authority to enter into this agreement
                  </Typography>
                </Box>
                <Box component="li">
                  <Typography sx={{ color: isLight ? '#64748b' : '#94a3b8' }}>
                    You are at least 18 years old or have parental/guardian consent
                  </Typography>
                </Box>
                <Box component="li">
                  <Typography sx={{ color: isLight ? '#64748b' : '#94a3b8' }}>
                    You represent an organization with authority to bind that entity
                  </Typography>
                </Box>
                <Box component="li">
                  <Typography sx={{ color: isLight ? '#64748b' : '#94a3b8' }}>
                    You agree to comply with all applicable laws and regulations
                  </Typography>
                </Box>
              </Box>

              <Typography paragraph sx={{ color: isLight ? '#64748b' : '#94a3b8' }}>
                If you do not agree with any part of these Terms, you must discontinue use of our Services immediately.
              </Typography>
            </Card>
          </Grid>

          {/* Section 2: Service Description */}
          <Grid item xs={12}>
            <Card
              elevation={0}
              sx={{
                background: isLight
                  ? 'rgba(255, 255, 255, 0.9)'
                  : 'rgba(15, 23, 42, 0.8)',
                backdropFilter: 'blur(20px)',
                border: isLight
                  ? '1px solid rgba(99, 102, 241, 0.1)'
                  : '1px solid rgba(16, 185, 129, 0.2)',
                borderRadius: 3,
                p: 4
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                <Avatar
                  sx={{
                    width: 48,
                    height: 48,
                    background: isLight ? 'linear-gradient(135deg, #6366f1 0%, #1e3a5f 100%)' : 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
                    mr: 2
                  }}
                >
                  <Business sx={{ color: 'white' }} />
                </Avatar>
                <Typography variant="h5" component="h2" sx={{ fontWeight: 700, color: isLight ? '#1e293b' : '#ffffff' }}>
                  2. Service Description & Scope
                </Typography>
              </Box>
              
              <Typography paragraph sx={{ color: isLight ? '#64748b' : '#94a3b8', mb: 3 }}>
                Alphanetix provides enterprise-grade integration and automation solutions, including:
              </Typography>
              
              <Grid container spacing={3}>
                <Grid item xs={12} md={6}>
                  <Box sx={{ mb: 3 }}>
                    <Typography variant="h6" sx={{ color: isLight ? '#1e293b' : '#ffffff', mb: 2, fontWeight: 600 }}>
                      Core Platform Services
                    </Typography>
                    <Box component="ul" sx={{ pl: 3, '& li': { mb: 1 } }}>
                      <Box component="li">
                        <Typography variant="body2" sx={{ color: isLight ? '#64748b' : '#94a3b8' }}>
                          No-code integration platform and tools
                        </Typography>
                      </Box>
                      <Box component="li">
                        <Typography variant="body2" sx={{ color: isLight ? '#64748b' : '#94a3b8' }}>
                          Pre-built connectors and API integrations
                        </Typography>
                      </Box>
                      <Box component="li">
                        <Typography variant="body2" sx={{ color: isLight ? '#64748b' : '#94a3b8' }}>
                          Workflow automation and business process management
                        </Typography>
                      </Box>
                      <Box component="li">
                        <Typography variant="body2" sx={{ color: isLight ? '#64748b' : '#94a3b8' }}>
                          Real-time monitoring and analytics dashboard
                        </Typography>
                      </Box>
                    </Box>
                  </Box>
                </Grid>
                
                <Grid item xs={12} md={6}>
                  <Box sx={{ mb: 3 }}>
                    <Typography variant="h6" sx={{ color: isLight ? '#1e293b' : '#ffffff', mb: 2, fontWeight: 600 }}>
                      Support & Professional Services
                    </Typography>
                    <Box component="ul" sx={{ pl: 3, '& li': { mb: 1 } }}>
                      <Box component="li">
                        <Typography variant="body2" sx={{ color: isLight ? '#64748b' : '#94a3b8' }}>
                          24/7 technical support and customer service
                        </Typography>
                      </Box>
                      <Box component="li">
                        <Typography variant="body2" sx={{ color: isLight ? '#64748b' : '#94a3b8' }}>
                          Implementation and onboarding assistance
                        </Typography>
                      </Box>
                      <Box component="li">
                        <Typography variant="body2" sx={{ color: isLight ? '#64748b' : '#94a3b8' }}>
                          Custom integration development services
                        </Typography>
                      </Box>
                      <Box component="li">
                        <Typography variant="body2" sx={{ color: isLight ? '#64748b' : '#94a3b8' }}>
                          Training programs and educational resources
                        </Typography>
                      </Box>
                    </Box>
                  </Box>
                </Grid>
              </Grid>

              <Typography paragraph sx={{ color: isLight ? '#64748b' : '#94a3b8' }}>
                We reserve the right to modify, suspend, or discontinue any aspect of our Services with appropriate notice to users.
              </Typography>
            </Card>
          </Grid>

          {/* Section 3-8: Additional sections */}
          <Grid item xs={12}>
            <Card
              elevation={0}
              sx={{
                background: isLight
                  ? 'rgba(255, 255, 255, 0.9)'
                  : 'rgba(15, 23, 42, 0.8)',
                backdropFilter: 'blur(20px)',
                border: isLight
                  ? '1px solid rgba(99, 102, 241, 0.1)'
                  : '1px solid rgba(16, 185, 129, 0.2)',
                borderRadius: 3,
                p: 4
              }}
            >
              {/* User Accounts & Responsibilities */}
              <Box sx={{ mb: 5 }}>
                <Typography variant="h5" component="h2" sx={{ fontWeight: 700, color: isLight ? '#1e293b' : '#ffffff', mb: 3 }}>
                  3. User Accounts & Responsibilities
                </Typography>
                
                <Typography paragraph sx={{ color: isLight ? '#64748b' : '#94a3b8', mb: 3 }}>
                  To access certain features, you must create an account and maintain accurate, current information:
                </Typography>
                
                <Box component="ul" sx={{ pl: 4, mb: 3, '& li': { mb: 2 } }}>
                  <Box component="li">
                    <Typography sx={{ color: isLight ? '#64748b' : '#94a3b8' }}>
                      <strong style={{ color: isLight ? '#1e293b' : '#ffffff' }}>Account Security:</strong> You are responsible for maintaining the confidentiality of your login credentials and all activities under your account
                    </Typography>
                  </Box>
                  <Box component="li">
                    <Typography sx={{ color: isLight ? '#64748b' : '#94a3b8' }}>
                      <strong style={{ color: isLight ? '#1e293b' : '#ffffff' }}>Accurate Information:</strong> Provide truthful, accurate, and complete information during registration and keep it updated
                    </Typography>
                  </Box>
                  <Box component="li">
                    <Typography sx={{ color: isLight ? '#64748b' : '#94a3b8' }}>
                      <strong style={{ color: isLight ? '#1e293b' : '#ffffff' }}>Authorized Use:</strong> Ensure only authorized personnel access your account and comply with your organization's security policies
                    </Typography>
                  </Box>
                  <Box component="li">
                    <Typography sx={{ color: isLight ? '#64748b' : '#94a3b8' }}>
                      <strong style={{ color: isLight ? '#1e293b' : '#ffffff' }}>Immediate Notification:</strong> Report any unauthorized use or security breaches to our support team immediately
                    </Typography>
                  </Box>
                </Box>
              </Box>

              {/* Acceptable Use Policy */}
              <Box sx={{ mb: 5 }}>
                <Typography variant="h5" component="h2" sx={{ fontWeight: 700, color: isLight ? '#1e293b' : '#ffffff', mb: 3 }}>
                  4. Acceptable Use Policy
                </Typography>
                
                <Typography paragraph sx={{ color: isLight ? '#64748b' : '#94a3b8', mb: 3 }}>
                  You agree not to use our Services for any unlawful, harmful, or prohibited activities, including but not limited to:
                </Typography>
                
                <Grid container spacing={2}>
                  <Grid item xs={12} md={6}>
                    <Box
                      sx={{
                        p: 3,
                        background: isLight ? '#fef2f2' : 'rgba(239, 68, 68, 0.1)',
                        borderRadius: 2,
                        border: `1px solid ${isLight ? '#fecaca' : 'rgba(239, 68, 68, 0.2)'}`
                      }}
                    >
                      <Typography variant="h6" sx={{ fontWeight: 600, color: '#dc2626', mb: 2 }}>
                        Prohibited Activities
                      </Typography>
                      <Box component="ul" sx={{ pl: 2, '& li': { mb: 1 } }}>
                        <Box component="li">
                          <Typography variant="body2" sx={{ color: isLight ? '#64748b' : '#94a3b8' }}>
                            Violating laws, regulations, or third-party rights
                          </Typography>
                        </Box>
                        <Box component="li">
                          <Typography variant="body2" sx={{ color: isLight ? '#64748b' : '#94a3b8' }}>
                            Transmitting malicious code or harmful content
                          </Typography>
                        </Box>
                        <Box component="li">
                          <Typography variant="body2" sx={{ color: isLight ? '#64748b' : '#94a3b8' }}>
                            Attempting unauthorized access to systems
                          </Typography>
                        </Box>
                        <Box component="li">
                          <Typography variant="body2" sx={{ color: isLight ? '#64748b' : '#94a3b8' }}>
                            Interfering with service operation or security
                          </Typography>
                        </Box>
                      </Box>
                    </Box>
                  </Grid>
                  
                  <Grid item xs={12} md={6}>
                    <Box
                      sx={{
                        p: 3,
                        background: isLight ? '#f0fdf4' : 'rgba(16, 185, 129, 0.1)',
                        borderRadius: 2,
                        border: `1px solid ${isLight ? '#bbf7d0' : 'rgba(16, 185, 129, 0.2)'}`
                      }}
                    >
                      <Typography variant="h6" sx={{ fontWeight: 600, color: isLight ? '#6366f1' : '#059669', mb: 2 }}>
                        Encouraged Practices
                      </Typography>
                      <Box component="ul" sx={{ pl: 2, '& li': { mb: 1 } }}>
                        <Box component="li">
                          <Typography variant="body2" sx={{ color: isLight ? '#64748b' : '#94a3b8' }}>
                            Following security best practices
                          </Typography>
                        </Box>
                        <Box component="li">
                          <Typography variant="body2" sx={{ color: isLight ? '#64748b' : '#94a3b8' }}>
                            Respecting intellectual property rights
                          </Typography>
                        </Box>
                        <Box component="li">
                          <Typography variant="body2" sx={{ color: isLight ? '#64748b' : '#94a3b8' }}>
                            Providing constructive feedback
                          </Typography>
                        </Box>
                        <Box component="li">
                          <Typography variant="body2" sx={{ color: isLight ? '#64748b' : '#94a3b8' }}>
                            Using services for legitimate business purposes
                          </Typography>
                        </Box>
                      </Box>
                    </Box>
                  </Grid>
                </Grid>
              </Box>

              {/* Intellectual Property */}
              <Box sx={{ mb: 5 }}>
                <Typography variant="h5" component="h2" sx={{ fontWeight: 700, color: isLight ? '#1e293b' : '#ffffff', mb: 3 }}>
                  5. Intellectual Property Rights
                </Typography>
                
                <Typography paragraph sx={{ color: isLight ? '#64748b' : '#94a3b8', mb: 3 }}>
                  All intellectual property rights in our Services, including software, content, trademarks, and proprietary methodologies, belong to Alphanetix or our licensors. You receive a limited, non-exclusive license to use our Services subject to these Terms.
                </Typography>
              </Box>

              {/* Service Level Agreement */}
              <Box sx={{ mb: 5 }}>
                <Typography variant="h5" component="h2" sx={{ fontWeight: 700, color: isLight ? '#1e293b' : '#ffffff', mb: 3 }}>
                  6. Service Level Agreement & Availability
                </Typography>
                
                <Typography paragraph sx={{ color: isLight ? '#64748b' : '#94a3b8', mb: 3 }}>
                  We strive to maintain high service availability and performance standards. Our current service level commitments include:
                </Typography>
                
                <Grid container spacing={2}>
                  <Grid item xs={12} md={4}>
                    <Box sx={{ textAlign: 'center', p: 2 }}>
                      <Typography variant="h4" sx={{ fontWeight: 800, color: isLight ? '#6366f1' : '#10b981', mb: 1 }}>
                        99.9%
                      </Typography>
                      <Typography variant="body2" sx={{ color: isLight ? '#64748b' : '#94a3b8' }}>
                        Uptime Guarantee
                      </Typography>
                    </Box>
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <Box sx={{ textAlign: 'center', p: 2 }}>
                      <Typography variant="h4" sx={{ fontWeight: 800, color: isLight ? '#6366f1' : '#10b981', mb: 1 }}>
                        24/7
                      </Typography>
                      <Typography variant="body2" sx={{ color: isLight ? '#64748b' : '#94a3b8' }}>
                        Support Coverage
                      </Typography>
                    </Box>
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <Box sx={{ textAlign: 'center', p: 2 }}>
                      <Typography variant="h4" sx={{ fontWeight: 800, color: isLight ? '#6366f1' : '#10b981', mb: 1 }}>
                        &lt;2hr
                      </Typography>
                      <Typography variant="body2" sx={{ color: isLight ? '#64748b' : '#94a3b8' }}>
                        Response Time
                      </Typography>
                    </Box>
                  </Grid>
                </Grid>
              </Box>

              {/* Limitation of Liability */}
              <Box sx={{ mb: 5 }}>
                <Typography variant="h5" component="h2" sx={{ fontWeight: 700, color: isLight ? '#1e293b' : '#ffffff', mb: 3 }}>
                  7. Limitation of Liability & Disclaimers
                </Typography>
                
                <Typography paragraph sx={{ color: isLight ? '#64748b' : '#94a3b8', mb: 3 }}>
                  TO THE MAXIMUM EXTENT PERMITTED BY LAW, ALPHANETIX SHALL NOT BE LIABLE FOR:
                </Typography>
                
                <Box component="ul" sx={{ pl: 4, mb: 3, '& li': { mb: 1 } }}>
                  <Box component="li">
                    <Typography sx={{ color: isLight ? '#64748b' : '#94a3b8' }}>
                      Indirect, incidental, special, consequential, or punitive damages
                    </Typography>
                  </Box>
                  <Box component="li">
                    <Typography sx={{ color: isLight ? '#64748b' : '#94a3b8' }}>
                      Loss of profits, data, use, goodwill, or other intangible losses
                    </Typography>
                  </Box>
                  <Box component="li">
                    <Typography sx={{ color: isLight ? '#64748b' : '#94a3b8' }}>
                      Damages resulting from third-party actions or content
                    </Typography>
                  </Box>
                  <Box component="li">
                    <Typography sx={{ color: isLight ? '#64748b' : '#94a3b8' }}>
                      Service interruptions due to force majeure events
                    </Typography>
                  </Box>
                </Box>
              </Box>

              {/* Termination */}
              <Box sx={{ mb: 5 }}>
                <Typography variant="h5" component="h2" sx={{ fontWeight: 700, color: isLight ? '#1e293b' : '#ffffff', mb: 3 }}>
                  8. Termination
                </Typography>
                
                <Typography paragraph sx={{ color: isLight ? '#64748b' : '#94a3b8', mb: 3 }}>
                  Either party may terminate this agreement with appropriate notice. Upon termination, your access to Services will cease, and you must comply with data export requirements within the specified timeframe.
                </Typography>
              </Box>

              {/* Governing Law */}
              <Box>
                <Typography variant="h5" component="h2" sx={{ fontWeight: 700, color: isLight ? '#1e293b' : '#ffffff', mb: 3 }}>
                  9. Governing Law & Dispute Resolution
                </Typography>
                
                <Typography paragraph sx={{ color: isLight ? '#64748b' : '#94a3b8' }}>
                  These Terms shall be governed by the laws of Delaware, United States. Any disputes will be resolved through binding arbitration in accordance with the Commercial Arbitration Rules of the American Arbitration Association.
                </Typography>
              </Box>
            </Card>
          </Grid>
        </Grid>

        {/* Contact Section */}
        <Card
          elevation={0}
          sx={{
            mt: 6,
            mb: 4,
            background: isLight
              ? 'linear-gradient(135deg, #f8fafc 0%, #e2e8f0 100%)'
              : 'linear-gradient(135deg, rgba(16, 185, 129, 0.1) 0%, rgba(15, 23, 42, 0.8) 100%)',
            border: `2px solid ${isLight ? 'rgba(99, 102, 241, 0.2)' : 'rgba(16, 185, 129, 0.3)'}`,
            borderRadius: 3,
            p: 4
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
            <Avatar
              sx={{
                width: 56,
                height: 56,
                background: isLight ? 'linear-gradient(135deg, #6366f1 0%, #1e3a5f 100%)' : 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
                mr: 3
              }}
            >
              <ContactMail sx={{ color: 'white', fontSize: '1.8rem' }} />
            </Avatar>
            <Box>
              <Typography variant="h5" sx={{ fontWeight: 700, color: isLight ? '#1e293b' : '#ffffff', mb: 1 }}>
                Questions About These Terms?
              </Typography>
              <Typography sx={{ color: isLight ? '#64748b' : '#94a3b8' }}>
                Our legal and support teams are available to clarify any provisions
              </Typography>
            </Box>
          </Box>
          
          <Grid container spacing={3}>
            <Grid item xs={12} md={4}>
              <Box sx={{ textAlign: 'center', p: 2 }}>
                <Typography variant="h6" sx={{ fontWeight: 600, color: isLight ? '#1e293b' : '#ffffff', mb: 1 }}>
                  Legal Inquiries
                </Typography>
                <Typography sx={{ color: isLight ? '#64748b' : '#94a3b8' }}>
                  legal@alphanetix.com
                </Typography>
              </Box>
            </Grid>
            <Grid item xs={12} md={4}>
              <Box sx={{ textAlign: 'center', p: 2 }}>
                <Typography variant="h6" sx={{ fontWeight: 600, color: isLight ? '#1e293b' : '#ffffff', mb: 1 }}>
                  Support Team
                </Typography>
                <Typography sx={{ color: isLight ? '#64748b' : '#94a3b8' }}>
                  support@alphanetix.com
                </Typography>
              </Box>
            </Grid>
            <Grid item xs={12} md={4}>
              <Box sx={{ textAlign: 'center', p: 2 }}>
                <Typography variant="h6" sx={{ fontWeight: 600, color: isLight ? '#1e293b' : '#ffffff', mb: 1 }}>
                  Phone Support
                </Typography>
                <Typography sx={{ color: isLight ? '#64748b' : '#94a3b8' }}>
                  (855) 314-1529
                </Typography>
              </Box>
            </Grid>
          </Grid>
        </Card>
        
        {/* Legal Disclaimer */}
        <Card
          elevation={0}
          sx={{
            background: isLight
              ? 'rgba(255, 193, 7, 0.1)'
              : 'rgba(255, 193, 7, 0.15)',
            border: `1px solid ${isLight ? 'rgba(255, 193, 7, 0.3)' : 'rgba(255, 193, 7, 0.4)'}`,
            borderRadius: 3,
            p: 3
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'flex-start' }}>
            <Warning sx={{ color: '#ffc107', mr: 2, mt: 0.5, fontSize: '1.5rem' }} />
            <Box>
              <Typography variant="h6" sx={{ fontWeight: 600, color: isLight ? '#1e293b' : '#ffffff', mb: 1 }}>
                Important Legal Notice
              </Typography>
              <Typography variant="body2" sx={{ color: isLight ? '#64748b' : '#94a3b8', lineHeight: 1.6 }}>
                These terms of service are designed to protect both parties while enabling productive business relationships. 
                Laws and regulations vary by jurisdiction. We recommend consulting with qualified legal counsel to understand 
                how these terms apply in your specific business context and geographic location.
              </Typography>
            </Box>
          </Box>
        </Card>
      </Container>
    </Box>
  );
}

export default TermsOfService;